import {
  Chip,
  createStyles,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  DeleteRounded,
  EditRounded,
  FileCopyOutlined
} from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { RootState } from '../app/store';
import { ORDER } from '../common/enum/order';
import displayRemovedUser from '../functions/displayRemovedUser';
import { useAppDispatch } from '../hooks/useAppRudux';
import useProjectUsers from '../hooks/useProjectUsers';
import {
  deleteProjectWorkflowAsync,
  ProjectWorkflow
} from '../pages/ProjectWorkflow/projectWorkflowSlice';
import Card from './Card';
import ConfirmDelete from './ConfirmDelete';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullList: {
      width: 'auto'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    item75: {
      width: '75%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        paddingLeft: theme.spacing(2)
      }
    },
    item25: {
      width: '25%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    chip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 13
    }
  })
);

interface ParamProps {
  projectId?: number;
  sortBy?: string;
  orderBy?: ORDER;
}

interface FlowCardProps<T> {
  renderFlows: ProjectWorkflow[];
  params: T;
  loadingElement: (params: T) => void;
}

export default function WorkflowCard<T extends ParamProps>({
  renderFlows,
  params,
  loadingElement
}: FlowCardProps<T>) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );
  const users = useProjectUsers(); // Environment-aware user selection

  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  const [openOption, setOpenOption] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<ProjectWorkflow | null>(
    null
  );
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  function toggleDrawer(open: boolean, flow?: ProjectWorkflow) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && flow) {
        setSelectedFlow(flow);
      }
      setOpenOption(open);
    };
  }

  function handleClickDelete() {
    setOpenDelete(!openDelete);
  }

  async function handleDeleteFlow() {
    if (selectedFlow) {
      dispatch(
        deleteProjectWorkflowAsync({
          workflowId: selectedFlow.id,
          workflowParams: params
        })
      );
      setOpenDelete(false);
    }
  }

  return (
    <div>
      <ConfirmDelete
        open={openDelete}
        text={selectedFlow?.name}
        onCloseDelete={handleClickDelete}
        onDelete={handleDeleteFlow}
      />
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={`${selectedFlow?.name} (${disciplines.find(
                  (discipline) => discipline.id === selectedFlow?.disciplineId
                )?.name
                  })`}
              />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              component={NavLink}
              to={`/project-workflows/create?projectId=${selectedFlow?.project?.id}&refFlowId=${selectedFlow?.id}`}
              button
            >
              <ListItemIcon>
                <FileCopyOutlined />
              </ListItemIcon>
              <ListItemText primary={t('workflowCard.copy') as string} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/project-workflows/${selectedFlow?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('workflowCard.edit') as string} />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem button onClick={handleClickDelete}>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText primary={t('workflowCard.delete') as string} />
            </ListItem>
          </List>
        </div>
      </Drawer>
      {renderFlows?.map((flow) => {
        return (
          <Card
            key={flow.id}
            to={`/project-workflows/${flow.id}/edit`}
            openDrawer={toggleDrawer(true, flow)}
          >
            <div className={classes.item25}>
              <Typography variant="body1">
                {flow.discipline && (
                  <Tooltip title={flow.discipline.cfihosName} arrow>
                    <Chip
                      className={classes.chip}
                      component="span"
                      label={flow.discipline.name}
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                )}
                {flow.name}
              </Typography>
            </div>
            <div className={classes.item75}>
              {flow.projectWorkflowSteps.map((projectWorkflowStep) => {
                const workflow = workflowSteps.find(
                  (workflowStep) =>
                    workflowStep.id === projectWorkflowStep.workflowStepId
                );
                return (
                  <div key={projectWorkflowStep.id}>
                    <Typography variant="body2">
                      <b>{workflow?.name}: </b>
                      {projectWorkflowStep.projectApprovalWorkflowSteps
                        .map((projectApprovalWorkflowStep) => {
                          const user = users.find(
                            (user) =>
                              user.id === projectApprovalWorkflowStep.userId
                          );
                          return displayRemovedUser(
                            projectApprovalWorkflowStep.userId,
                            user
                          );
                        })
                        .join(', ')}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
